import { useEffect, useRef, useState } from "react"
import useComponentSize from "@rehooks/component-size"
// For color interpolation, we use d3.interpolateRgb to blend between #FFFFFF (white) and #0000FF (blue)
// This creates a smooth gradient of 10 colors, one for each NFT size
// Importing d3.interpolateRgb here (assumes d3-interpolate installed and available in project)
import { interpolateRgb } from "d3-interpolate"
import { differenceInWeeks, fromUnixTime } from "date-fns"

import type { ILock } from "@/domains/lock"
import { formatCurrency, formatNumber } from "@/domains/library"
import useEcharts from "@/hooks/useEcharts"

interface VenammCurveChartProps {
  quote: {
    offer: bigint
    value: number
    haircut: number
    rate: number
    canSell: boolean
  }
  lock: ILock
}

// Simplified chart data - in a real implementation, you'd load this from a JSON file
// This creates approximate curves for different NFT sizes
const generateChartData = () => {
  const sizes = [100, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000, 250000, 500000]
  const weeks = Array.from({ length: 25 }, (_, i) => i)

  return {
    sizes,
    values: sizes.map((size) => {
      return weeks.map((week) => {
        // Simplified rate calculation: rate improves as weeks decrease
        // Smaller NFTs and closer to expiry = better rate
        const baseRate = 0.3 + (24 - week) * 0.025 // Rate improves from 0.3 to 0.9
        const sizeFactor = Math.max(0.7, 1 - size / 1000000) // Larger NFTs get slightly worse rates
        return [week, Math.min(0.99, baseRate * sizeFactor)]
      })
    }),
  }
}

// Generate 10 colors from white to blue for the NFT size series
const nftColors: string[] = Array.from({ length: 13 }, (_, i) => interpolateRgb("#4D75FF", "#DBE4FF")(i / 13))

export const VenammCurveChart = ({ quote, lock }: VenammCurveChartProps) => {
  const chart = useRef<HTMLDivElement | null>(null)
  const echarts = useEcharts()
  const size = useComponentSize(chart)
  const [chartInstance, setChartInstance] = useState<any>(null)

  const chartData = generateChartData()
  const itemWeeks = chartData.values[0]?.map((item) => item[0]) ?? []
  const now = new Date()
  const expiryDate = fromUnixTime(lock.lockEnds)
  const expiryWeeks = lock.hasExpired ? 0 : Math.max(0, differenceInWeeks(expiryDate, now))

  const series: any[] = chartData.sizes.map((size: number, index: number) => {
    const itemData = chartData.values[index] ?? []
    const itemValues = itemData.map((item) => formatCurrency(item[1] as number, 4))
    const seriesColor = nftColors[index]

    const options: any = {
      name: `NFT size ${formatNumber(size)}`,
      data: itemValues,
      type: "line",
      smooth: true,
      color: seriesColor,
      lineStyle: {
        width: 1,
        opacity: 0.6,
        color: seriesColor,
      },
      itemStyle: {
        color: seriesColor,
      },
      symbol: "none",
    }

    // Add mark area for the current quote if this is the first series (smallest NFT)
    if (quote && lock && index === 0 && expiryWeeks >= 0 && expiryWeeks <= 24) {
      const xOffset = 0.5
      // For expired locks, position the bar at x=0
      const barPosition = expiryWeeks === 0 ? 0 : expiryWeeks - 1
      options.markArea = {
        silent: true,
        data: [
          [
            {
              name: "Quote",
              xAxis: barPosition - xOffset,
              yAxis: 0,
            },
            {
              xAxis: barPosition + xOffset,
              yAxis: quote.rate + 0.01,
            },
          ],
        ],
        label: { show: false },
        itemStyle: {
          color: "#4D75FF",
          opacity: 0.5,
        },
      }
    }

    return options
  })

  const option = {
    grid: {
      top: "4%",
      left: "11%",
      right: "4%",
      bottom: "8%",
      show: false,
    },
    tooltip: {
      trigger: "axis",
      responsive: false,
      // Set background color to 80% opacity black, keeping the same shade as before
      backgroundColor: "rgba(1, 2, 26, 0.6)",
      borderColor: "#FFFFFF",
      borderWidth: "0.8",
      textStyle: {
        color: "#FFFFFF",
      },
      axisPointer: {
        type: "cross",
        label: {
          backgroundColor: "#0000FF",
        },
      },
      formatter: function (params: any) {
        const xValue = params[0]?.axisValue ?? 0
        let result = ""

        if (Number(xValue) === 0) {
          result += `<div>Expired</div>`
        } else {
          result += `<div>${xValue - 1}-${xValue} ${xValue === 1 ? "Week" : "Weeks"} to expiry</div>`
        }

        params.forEach((item: any) => {
          result += `<div>${item.marker} ${item.seriesName}, rate = ${item.value}</div>`
        })
        return result
      },
    },
    xAxis: {
      type: "category",
      data: itemWeeks,
      splitLine: {
        show: false,
      },
      axisLabel: {
        show: true,
        color: "#7083A9",
      },
      axisLine: {
        lineStyle: {
          color: "#7083A9",
        },
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: 1,
      splitLine: {
        show: false,
      },
      axisLabel: {
        fontSize: "12px",
        color: "#7083A9",
        formatter: (value: number) => formatCurrency(value, 2),
      },
      axisLine: {
        lineStyle: {
          color: "#7083A9",
        },
      },
    },
    series,
  }

  function renderChart() {
    if (!echarts || !chart.current) {
      return
    }

    const renderInstance = echarts.getInstanceByDom(chart.current)

    if (renderInstance) {
      renderInstance.setOption(option)
    } else {
      const instance = echarts.init(chart.current, "equalizer")
      instance.setOption(option)
      setChartInstance(instance)
    }
  }

  useEffect(() => {
    renderChart()
    if (chartInstance != null) {
      chartInstance.resize({
        height: size.height,
      })
    }
  }, [size, lock, quote])

  return <div ref={chart} className="h-full w-full" />
}
